import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart, User, Search, Menu, X, ClipboardList, Store, Download, MessageCircle } from 'lucide-react';
import useAuthStore from '../store/authStore';
import useCartStore from '../store/cartStore';
import useWishlistStore from '../store/wishlistStore';
import useNotepadStore from '../store/notepadStore';
import useThemeStore from '../store/themeStore';
import { Button } from './ui/button';
import { Input } from './ui/input';
import NotificationBell from './NotificationBell';
import AnnouncementBanner from './AnnouncementBanner';

const Navbar = ({ maintenance = false }) => {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const user = useAuthStore(state => state.user);
  const logout = useAuthStore(state => state.logout);
  const getItemCount = useCartStore(state => state.getItemCount);
  const wishlistCount = useWishlistStore(state => state.wishlistIds.length);
  const fetchWishlist = useWishlistStore(state => state.fetchWishlist);
  const clearWishlist = useWishlistStore(state => state.clearWishlist);
  const notepadCount = useNotepadStore(state => state.notepadItems.length);
  const fetchNotepad = useNotepadStore(state => state.fetchNotepad);
  const clearNotepad = useNotepadStore(state => state.clearNotepad);
  const branding = useThemeStore(state => state.branding);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [showVendorLink, setShowVendorLink] = useState(false);

  // Fetch marketplace settings
  useEffect(() => {
    const fetchMarketplaceSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/marketplace`);
        if (response.ok) {
          const data = await response.json();
          setShowVendorLink(data.vendor_mode_enabled && data.show_become_vendor);
        }
      } catch (error) {
        console.log('Marketplace settings not available');
      }
    };
    fetchMarketplaceSettings();
  }, []);

  // PWA Install Detection
  useEffect(() => {
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  window.navigator.standalone === true;
    setIsAppInstalled(isPWA);

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsAppInstalled(true);
      setDeferredPrompt(null);
      // Track the app install
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/track-app-download`, {
        method: 'POST'
      }).catch(() => {});
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsAppInstalled(true);
      }
      setDeferredPrompt(null);
    } else {
      setShowInstallModal(true);
    }
  };

  // Fetch wishlist and notepad when user authenticates
  useEffect(() => {
    if (isAuthenticated) {
      fetchWishlist();
      fetchNotepad();
    } else {
      clearWishlist();
      clearNotepad();
    }
  }, [isAuthenticated, fetchWishlist, clearWishlist, fetchNotepad, clearNotepad]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    } else {
      navigate('/search');
    }
  };

  const handleSearchClick = () => {
    navigate('/search');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      {!maintenance && <AnnouncementBanner />}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
          {/* Logo & Brand */}
          <Link to="/" className="flex items-center gap-4 md:gap-6 group ml-4 md:ml-8">
            <img 
              src="/navbar-logo.png?v=2026032611" 
              alt={`${branding.shopName} Logo`} 
              className="h-12 w-auto md:h-14 group-hover:scale-105 transition-transform"
            />
          </Link>

          {/* Search Bar - Desktop */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md mx-8">
            <div className="relative w-full">
              <Input
                type="text"
                placeholder="Produkte suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={handleSearchClick}
                className="pr-10 cursor-pointer"
                data-testid="search-input"
              />
              <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2" data-testid="search-button">
                <Search className="h-5 w-5 text-gray-400 hover:text-[#8B7355]" />
              </button>
            </div>
          </form>

          {/* Desktop Navigation — hidden on lg+ (DesktopSidebar takes over) */}
          <div className={`hidden md:flex ${maintenance ? '' : 'lg:hidden'} items-center space-x-6`}>
            <Link
              to="/products"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#5D4E3C] hover:text-[#C4A084] transition-colors"
              data-testid="products-link"
            >
              <ShoppingCart className="h-4 w-4" />
              Shop
            </Link>
            <Link
              to="/collections"
              className="text-sm font-medium text-[#5D4E3C] hover:text-[#C4A084] transition-colors"
              data-testid="collections-link"
            >
              ✨ Kollektionen
            </Link>
            <Link
              to="/sale"
              className="inline-flex items-center gap-1 text-sm font-bold text-[#B0512E] hover:text-[#8B3D24] transition-colors"
              data-testid="sale-link"
            >
              <span className="px-1.5 py-0.5 bg-[#B0512E] text-white text-[10px] tracking-wider">SALE</span>
            </Link>
            <Link
              to="/blog"
              className="text-sm font-medium text-[#5D4E3C] hover:text-[#C4A084] transition-colors"
              data-testid="blog-link"
            >
              Blog
            </Link>
            <Link
              to="/faq"
              className="text-sm font-medium text-[#5D4E3C] hover:text-[#C4A084] transition-colors"
              data-testid="faq-link"
            >
              FAQ
            </Link>
            {showVendorLink && (
              <Link 
                to="/become-vendor" 
                className="flex items-center gap-1.5 text-[#8B7355] hover:text-[#6D5A47] font-medium"
                data-testid="become-vendor-link"
              >
                <Store className="h-4 w-4" />
                Verkäufer werden
              </Link>
            )}
            
            {/* Wishlist-Herz: auch für Gäste sichtbar (LocalStorage), damit
                Kunden Lieblingsstücke ohne Login sammeln können. */}
            <Link to="/wishlist" className="relative" data-testid="wishlist-link">
              <Heart className="h-6 w-6 text-gray-700 hover:text-pink-500" />
              {wishlistCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-pink-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center" data-testid="wishlist-count">
                  {wishlistCount}
                </span>
              )}
            </Link>

            {isAuthenticated ? (
              <>
                <Link to="/notepad" className="relative" data-testid="notepad-link">
                  <ClipboardList className="h-6 w-6 text-gray-700 hover:text-blue-500" />
                  {notepadCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center" data-testid="notepad-count">
                      {notepadCount}
                    </span>
                  )}
                </Link>

                <Link to="/cart" className="relative" data-testid="cart-link">
                  <ShoppingCart className="h-6 w-6 text-gray-700 hover:text-gray-900" />
                  {getItemCount() > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center" data-testid="cart-count">
                      {getItemCount()}
                    </span>
                  )}
                </Link>
                
                <div className="relative group">
                  <button className="flex items-center space-x-1" data-testid="user-menu">
                    <User className="h-6 w-6 text-gray-700" />
                  </button>
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 hidden group-hover:block z-50">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      data-testid="profile-link"
                    >
                      Mein Profil
                    </Link>
                    <Link
                      to="/orders"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      data-testid="orders-link"
                    >
                      Meine Bestellungen
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      data-testid="logout-button"
                    >
                      Abmelden
                    </button>
                  </div>
                </div>
                
                {/* Notification Bell */}
                <NotificationBell />
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  className="text-sm font-medium text-[#5D4E3C] hover:text-[#C4A084] transition-colors"
                  data-testid="login-button"
                >
                  Anmelden
                </Link>
                <Link
                  to="/register"
                  className="inline-flex items-center px-4 py-2 text-xs font-semibold tracking-[2px] uppercase text-[#F5EDE4] bg-[#5D4E3C] hover:bg-[#4A3E32] transition-colors"
                  data-testid="register-button"
                >
                  Registrieren
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button and App Install */}
          <div className="md:hidden flex items-center gap-2">
            {/* App Install Button - nur anzeigen wenn nicht installiert */}
            {!isAppInstalled && !maintenance && (
              <button
                onClick={handleInstallClick}
                className="w-10 h-10 flex items-center justify-center border-2 border-[#C4A084] text-[#8B7355] hover:bg-[#C4A084] hover:text-white transition-colors"
                title="App installieren"
                data-testid="app-install-button"
              >
                <Download className="h-5 w-5" />
              </button>
            )}
            
            {/* Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-10 h-10 flex items-center justify-center border-2 border-[#C4A084] text-[#8B7355] hover:bg-[#C4A084] hover:text-white transition-colors"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Search */}
        <form onSubmit={handleSearch} className="md:hidden pb-4">
          <div className="relative">
            <Input
              type="text"
              placeholder="Produkte suchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pr-10"
            />
            <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2">
              <Search className="h-5 w-5 text-gray-400" />
            </button>
          </div>
        </form>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-4 space-y-4">
            {/* Prominent Vendor CTA - only show if enabled */}
            {showVendorLink && (
              <Link
                to="/become-vendor"
                className="flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-[#8B7355] to-[#A08060] text-white rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Store className="h-5 w-5" />
                Verkäufer werden
              </Link>
            )}
            
            <div className={showVendorLink ? "border-t pt-4" : ""}>
              <Link
                to="/products"
                className="block text-gray-700 hover:text-gray-900"
                onClick={() => setMobileMenuOpen(false)}
              >
                Shop
              </Link>
            </div>
            <Link
              to="/blog"
              className="block text-gray-700 hover:text-gray-900"
              onClick={() => setMobileMenuOpen(false)}
            >
              Blog
            </Link>
            <Link
              to="/faq"
              className="block text-gray-700 hover:text-gray-900"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-faq-link"
            >
              FAQ
            </Link>
            <Link
              to="/sale"
              className="flex items-center gap-2 text-[#B0512E] font-bold hover:text-[#8B3D24]"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-sale-link"
            >
              <span className="px-1.5 py-0.5 bg-[#B0512E] text-white text-[10px] tracking-wider">SALE</span>
              <span>Reduzierte Artikel</span>
            </Link>
            <Link
              to="/categories"
              className="block text-gray-700 hover:text-gray-900"
              onClick={() => setMobileMenuOpen(false)}
            >
              Kategorien
            </Link>
            
            {isAuthenticated ? (
              <>
                <Link
                  to="/wishlist"
                  className="block text-gray-700 hover:text-gray-900"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Favoritenliste
                </Link>
                <Link
                  to="/cart"
                  className="block text-gray-700 hover:text-gray-900"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Warenkorb ({getItemCount()})
                </Link>
                <Link
                  to="/orders"
                  className="block text-gray-700 hover:text-gray-900"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Meine Bestellungen
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="block text-gray-700 hover:text-gray-900"
                >
                  Abmelden
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button variant="outline" className="w-full">Anmelden</Button>
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button className="w-full">Registrieren</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      )}

      {/* App Install Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#C4A084] flex items-center justify-center">
                    <Download className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900">App installieren</h3>
                    <p className="text-sm text-gray-500">Homeyoursoul auf Ihrem Gerät</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowInstallModal(false)}
                  className="p-2 hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-lg">Installation:</h4>
                <ol className="list-decimal list-inside space-y-3 text-gray-700">
                  <li>Tippen Sie auf das <strong>Teilen-Symbol</strong> (Quadrat mit Pfeil nach oben) in Safari</li>
                  <li>Scrollen Sie und tippen Sie auf <strong>"Zum Home-Bildschirm"</strong></li>
                  <li>Tippen Sie auf <strong>"Hinzufügen"</strong></li>
                </ol>
              </div>

              <div className="mt-6 pt-4 border-t space-y-2">
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <span className="text-green-500">✓</span>
                  <span>Kein App Store nötig</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <span className="text-green-500">✓</span>
                  <span>Weniger Speicherplatz</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <span className="text-green-500">✓</span>
                  <span>Immer aktuell</span>
                </div>
              </div>

              <Button
                onClick={() => setShowInstallModal(false)}
                className="w-full mt-6 bg-[#8B7355] hover:bg-[#7A6548] rounded-none"
              >
                Verstanden
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

export default Navbar;